import axios, { type AxiosResponse } from 'axios'
import { orderBy } from 'lodash'
import type { Ref } from 'vue'

import type { PaginatedResponse } from '@/api/schemas/common/pagination'
import { API_ENDPOINTS } from '@/constants/apiEndpoints'
import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'
import { handleApiError } from '@/utils/apiHelpers'

import type { DashboardState } from './state'
import type {
    DashboardFilters,
    DashboardWidget,
    DashboardWidgetBody,
    OfflineOrderNotSentDetailRow,
    SliceLoadingCodeName
} from './types'

function dashboardWidgetIdsEqual(a: unknown, b: unknown): boolean {
    if (a === undefined || a === null || b === undefined || b === null) return false
    return String(a) === String(b)
}

function orderDashboardWidgets(items: DashboardWidget[]): DashboardWidget[] {
    return orderBy(
        items,
        [
            (w: DashboardWidget) => {
                const p = w?.position
                if (p === null || p === undefined || p === '') return Number.POSITIVE_INFINITY
                const n = Number(p)
                return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY
            },
            (w: DashboardWidget) => {
                const id = w?.id
                if (id === null || id === undefined || id === '') return 0
                const n = Number(id)
                return Number.isFinite(n) ? n : 0
            }
        ],
        ['asc', 'asc']
    )
}

function isPlainObjectBody(body: unknown): body is DashboardWidgetBody {
    return Boolean(body && typeof body === 'object' && !Array.isArray(body))
}

function mergeDashboardItemPayload(requestItem: DashboardWidget | null | undefined, data: DashboardWidget): DashboardWidget {
    const prevBody = isPlainObjectBody(requestItem?.body) ? requestItem.body : {}
    const nextBodyRaw = isPlainObjectBody(data?.body) ? data.body : {}
    const mergedBody: DashboardWidgetBody = { ...prevBody, ...nextBodyRaw }

    if (mergedBody.title == null || mergedBody.title === '') {
        mergedBody.title = prevBody.title ?? nextBodyRaw.title ?? requestItem?.title ?? data?.title
    }

    return {
        ...(requestItem || {}),
        ...data,
        id: data?.id ?? requestItem?.id,
        codeName: data?.codeName ?? requestItem?.codeName,
        redirectPermission: data?.redirectPermission ?? requestItem?.redirectPermission,
        redirectDestination: data?.redirectDestination ?? requestItem?.redirectDestination,
        title: data?.title ?? requestItem?.title,
        body: mergedBody,
        loading: false,
        loadFailed: false
    }
}

export function createDashboardActions(state: DashboardState) {
    // Incremented on clearDashboardItems (logout / dashboard re-init) so responses of
    // requests started in a previous session are discarded instead of resurrecting
    // widgets the current user has no access to.
    let dashboardRequestGeneration = 0

    const sliceLoadingByCodeName: Partial<Record<SliceLoadingCodeName, Ref<boolean>>> = {
        paperNeededReport: state.paperNeededLoading,
        userTasksNotFinished: state.userTasksLoading,
        ordersProcesses: state.ordersProcessesLoading,
        processedOrders: state.processedOrdersLoading,
        newOrders: state.newOrdersLoading
    }

    function setWidgetSliceLoading(codeName: string | undefined, value: boolean) {
        if (!codeName) return
        const refLoading = sliceLoadingByCodeName[codeName as SliceLoadingCodeName]
        if (refLoading) refLoading.value = value
    }

    function upsertDashboardItem(dashboardItem: DashboardWidget) {
        const currentItems = Array.isArray(state.dashboardItems.value) ? [...state.dashboardItems.value] : []
        const currentIndex = currentItems.findIndex((item) => dashboardWidgetIdsEqual(item?.id, dashboardItem?.id))

        if (currentIndex === -1) {
            currentItems.push(dashboardItem)
        } else {
            currentItems.splice(currentIndex, 1, {
                ...currentItems[currentIndex],
                ...dashboardItem
            })
        }

        state.dashboardItems.value = orderDashboardWidgets(currentItems)
    }

    function appendQueryToPath(paginate = '', query = ''): string {
        if (!query) return paginate || ''
        let part = query
        if (part.startsWith('?')) part = part.slice(1)
        if (part.startsWith('&')) part = part.slice(1)
        if (!part) return paginate || ''
        const base = paginate || ''
        const joiner = base.includes('?') ? '&' : '?'
        return `${base}${joiner}${part}`
    }

    function getItemQuerySuffix(query = ''): string {
        return appendQueryToPath('', query)
    }

    function handleDashboardItemRequest(item: DashboardWidget | undefined) {
        if (item?.id != null && item.id !== '') {
            upsertDashboardItem({
                ...item,
                loadFailed: false
            })
        }
        setWidgetSliceLoading(item?.codeName, true)
    }

    function handleDashboardItemFailure(item: DashboardWidget | undefined) {
        if (item?.id != null && item.id !== '') {
            upsertDashboardItem({
                ...item,
                loading: false,
                loadFailed: true
            })
        }
        setWidgetSliceLoading(item?.codeName, false)
    }

    function readBodyDataResults(body: DashboardWidgetBody | undefined): unknown[] {
        const d = body?.data
        if (d && typeof d === 'object' && !Array.isArray(d) && 'results' in d) {
            const r = (d as { results: unknown }).results
            return Array.isArray(r) ? r : []
        }
        return []
    }

    function readBodyContentArray(body: DashboardWidgetBody | undefined): unknown[] {
        const c = body?.content
        return Array.isArray(c) ? c : []
    }

    function applyWidgetDetailToSlices(merged: DashboardWidget) {
        const body = merged?.body
        switch (merged.codeName) {
            case 'paperNeededReport':
                state.paperNeeded.value = readBodyDataResults(body)
                state.paperNeededLoading.value = false
                break
            case 'userTasksNotFinished':
                state.userTasks.value = readBodyDataResults(body)
                state.userTasksLoading.value = false
                break
            case 'ordersProcesses':
                state.ordersProcesses.value = readBodyContentArray(body)
                state.ordersProcessesLoading.value = false
                break
            case 'processedOrders':
                state.processedOrders.value = readBodyContentArray(body)
                state.processedOrdersLoading.value = false
                break
            case 'newOrders':
                state.newOrders.value = readBodyContentArray(body)
                state.newOrdersLoading.value = false
                break
            case 'numberOfOrdersPerImposition': {
                const raw = body?.data
                state.numberOfOrdersPerImposition.value = Array.isArray(raw)
                    ? (raw as typeof state.numberOfOrdersPerImposition.value)
                    : []
                break
            }
            default:
                break
        }
    }

    function handleDashboardItemSuccess(data: DashboardWidget, requestItem: DashboardWidget | undefined) {
        const merged = mergeDashboardItemPayload(requestItem, data)

        if (merged.id != null && merged.id !== '') {
            upsertDashboardItem(merged)
        }

        applyWidgetDetailToSlices(merged)
    }

    async function getDashboardSections(): Promise<AxiosResponse> {
        const requestGeneration = dashboardRequestGeneration

        try {
            const response = await axios.get(API_ENDPOINTS.DASHBOARD.SECTIONS)
            if (requestGeneration === dashboardRequestGeneration) {
                state.dashboardSections.value = response.data.results
            }
            return response
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.fetchSectionsFailed'))
            throw error
        }
    }

    async function getDashboardItems(): Promise<AxiosResponse> {
        try {
            return await axios.get(API_ENDPOINTS.DASHBOARD.ITEMS)
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.fetchItemsFailed'))
            throw error
        }
    }

    async function getDashboardItem({
        item = null,
        paginate = '',
        query = ''
    }: {
        item?: DashboardWidget | null
        paginate?: string
        query?: string
    } = {}): Promise<AxiosResponse> {
        if (!item) {
            throw new Error('getDashboardItem: item is required')
        }

        const requestGeneration = dashboardRequestGeneration
        handleDashboardItemRequest(item)

        try {
            const pathSuffix = appendQueryToPath(paginate, query)
            const response = await axios.get(`${API_ENDPOINTS.DASHBOARD.ITEMS}/${item.id}${pathSuffix}`)

            if (requestGeneration === dashboardRequestGeneration) {
                handleDashboardItemSuccess(response.data as DashboardWidget, item)
            }
            return response
        } catch (error) {
            if (requestGeneration !== dashboardRequestGeneration) {
                throw error
            }

            handleDashboardItemFailure(item)
            handleApiError(error, t('common.dashboard.errors.fetchWidgetFailed', { codeName: item?.codeName || '-' }))
            throw error
        }
    }

    async function getOrdersProductsPerDayNotSent({ query = '' } = {}): Promise<AxiosResponse> {
        try {
            const response = await axios.get(
                `${API_ENDPOINTS.DASHBOARD.ORDERS_PRODUCTS_PER_DAY_NOT_SENT}${getItemQuerySuffix(query)}`
            )
            state.ordersProductsPerDayNotSent.value = response.data.results || []
            return response
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.fetchOrdersNotSentDetailsFailed'))
            throw error
        }
    }

    async function getOrdersProductsPerDayNotSentByOrder({ query = '' } = {}): Promise<AxiosResponse> {
        try {
            return await axios.get(
                `${API_ENDPOINTS.DASHBOARD.ORDERS_PRODUCTS_PER_DAY_NOT_SENT_BY_ORDER}${getItemQuerySuffix(query)}`
            )
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.fetchOrdersNotSentDetailsFailed'))
            throw error
        }
    }

    async function getOfflineOrdersByProductType({
        query = ''
    }: {
        query?: string
    } = {}): Promise<AxiosResponse<PaginatedResponse<OfflineOrderNotSentDetailRow>>> {
        try {
            return await axios.get<PaginatedResponse<OfflineOrderNotSentDetailRow>>(
                `${API_ENDPOINTS.DASHBOARD.OFFLINE_ORDERS_BY_PRODUCT_TYPE}${getItemQuerySuffix(query)}`
            )
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.fetchOrdersNotSentDetailsFailed'))
            throw error
        }
    }

    async function generateOrdersProductsPerDayNotSentReport({ query = '', userEmail = '' } = {}): Promise<AxiosResponse> {
        try {
            const response = await axios.get(
                `${API_ENDPOINTS.DASHBOARD.ORDERS_PRODUCTS_PER_DAY_NOT_SENT}?report=true${
                    query ? `&${query.replace(/^\?/, '')}` : ''
                }`,
                { timeout: 120000 }
            )

            notificationService.success(t('common.dashboard.notifications.reportWillBeSent', { userEmail }))
            return response
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.generateReportFailed'))
            throw error
        }
    }

    async function getOrdersProductsDelayed({ query = '' } = {}): Promise<AxiosResponse> {
        try {
            const response = await axios.get(`${API_ENDPOINTS.DASHBOARD.ORDERS_PRODUCTS_DELAYED}${getItemQuerySuffix(query)}`)
            state.ordersProductsDelayed.value = response.data.results || []
            return response
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.fetchDelayedOrdersDetailsFailed'))
            throw error
        }
    }

    async function getOrdersProductsDelayedByOrder({ query = '' } = {}): Promise<AxiosResponse> {
        try {
            return await axios.get(`${API_ENDPOINTS.DASHBOARD.ORDERS_PRODUCTS_DELAYED_BY_ORDER}${getItemQuerySuffix(query)}`)
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.fetchDelayedOrdersDetailsFailed'))
            throw error
        }
    }

    async function generateOrdersDelayedReport({ query = '', userEmail = '' } = {}): Promise<AxiosResponse> {
        try {
            const response = await axios.get(
                `${API_ENDPOINTS.DASHBOARD.ORDERS_PRODUCTS_DELAYED}?report=true${query ? `&${query.replace(/^\?/, '')}` : ''}`,
                { timeout: 120000 }
            )

            notificationService.success(t('common.dashboard.notifications.reportWillBeSent', { userEmail }))
            return response
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.generateReportFailed'))
            throw error
        }
    }

    async function generateNumberOfOrdersPerImpositionReport({ query = '', userEmail = '' } = {}): Promise<AxiosResponse> {
        try {
            const response = await axios.get(
                `/jobs/imposition-parts-summaries${query ? `${query}&report=true` : '?report=true'}`,
                {
                    timeout: 120000
                }
            )

            notificationService.success(t('common.dashboard.notifications.reportWillBeSent', { userEmail }))
            return response
        } catch (error) {
            handleApiError(error, t('common.dashboard.errors.generateReportFailed'))
            throw error
        }
    }

    function initializeDashboardItems(items: DashboardWidget[] = []) {
        const nextItems = (Array.isArray(items) ? items : []).map((item) => ({
            ...item,
            loading: true,
            loadFailed: false
        }))

        state.dashboardItems.value = orderDashboardWidgets(nextItems)
    }

    function clearDashboardItems() {
        dashboardRequestGeneration += 1
        state.dashboardItems.value = []
    }

    function clearDashboardSections() {
        state.dashboardSections.value = []
    }

    function clearDashboardState() {
        clearDashboardItems()
        clearDashboardSections()
        state.paperNeeded.value = []
        state.paperNeededLoading.value = false
        state.userTasks.value = []
        state.userTasksLoading.value = false
        state.ordersProcesses.value = []
        state.ordersProcessesLoading.value = false
        state.processedOrders.value = []
        state.processedOrdersLoading.value = false
        state.newOrders.value = []
        state.newOrdersLoading.value = false
        state.numberOfOrdersPerImposition.value = []
        state.ordersProductsPerDayNotSent.value = []
        state.ordersProductsDelayed.value = []
        state.pagination.value = {}
        state.paginationSettings.value = {}
        state.setInitialFilters()
    }

    function setDashboardFilters(next: Partial<DashboardFilters>) {
        state.filters.value = { ...state.filters.value, ...next }
    }

    function clearDashboardFilters() {
        state.setInitialFilters()
    }

    function savePagination(data: { pagination?: Record<string, unknown>; paginationSettings?: Record<string, unknown> } | null) {
        state.pagination.value = data?.pagination || {}
        state.paginationSettings.value = data?.paginationSettings || {}
    }

    return {
        clearDashboardFilters,
        clearDashboardItems,
        clearDashboardSections,
        clearDashboardState,
        generateNumberOfOrdersPerImpositionReport,
        generateOrdersDelayedReport,
        generateOrdersProductsPerDayNotSentReport,
        getDashboardItem,
        getDashboardItems,
        getDashboardSections,
        getOfflineOrdersByProductType,
        getOrdersProductsDelayed,
        getOrdersProductsDelayedByOrder,
        getOrdersProductsPerDayNotSent,
        getOrdersProductsPerDayNotSentByOrder,
        initializeDashboardItems,
        savePagination,
        setDashboardFilters
    }
}
